import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogContent,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Edit, Add, Delete } from '@mui/icons-material'; // Import Delete icon
import { toast } from 'react-toastify';
import AddNotification from './AddNotification'; // Component for adding notifications
import EditNotification from './EditNotification'; // Component for editing notifications
import {BACKEND_BASE_URL} from "../config";
import api from '../components/api';

export default function NotificationList() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken'); // Retrieve the bearer token from local storage

      if (!token) {
        toast.error('Authentication token not found.');
        return;
      }

      const response = await api.get(`${BACKEND_BASE_URL}/api/notifications/`);
      

      const data = response.data;

      const transformedData = data.map((notification) => {
        const formattedSchedule = notification.schedule
          ? `${notification.schedule.slice(0, 10)} ${notification.schedule.slice(11, 16)}`
          : 'Immediate Notification';
        return {
          id: notification.id,
          type: notification.type.charAt(0).toUpperCase() + notification.type.slice(1),
          subject: notification.subject,
          context: notification.context,
          schedule: formattedSchedule,
          sent: notification.is_sent ? 'Sent' : 'Pending',
          recipients: notification.recipients,
        };
      });

      setNotifications(transformedData);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to fetch notifications.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditNotification = (notification) => {
    if (notification.sent === 'Sent') {
      toast.error('Cannot edit a sent notification.');
    } else {
      setCurrentNotification(notification);
      setEditDialogOpen(true);
    }
  };

  const handleDeleteNotification = async (notification) => {
    if (notification.sent === 'Sent') {
      toast.error('Cannot delete a sent notification.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await api.delete(`${BACKEND_BASE_URL}/api/notifications/${notification.id}/`);

      toast.success('Notification deleted successfully!');
      fetchNotifications(); // Refresh the list
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification.');
    }
  };

  const handleCloseDialogs = () => {
    setEditDialogOpen(false);
    setAddDialogOpen(false);
    setCurrentNotification(null);
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Notifications
      </Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={() => setAddDialogOpen(true)}
        style={{ marginBottom: '16px', float: 'right' }}
      >
        Add Notification
      </Button>
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Subject</TableCell> 
                <TableCell>Schedule</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
          {notifications.map((notification) => (
            <TableRow key={notification.id}>
              <TableCell>{notification.type}</TableCell>
              {/* <TableCell>
                <div
                  dangerouslySetInnerHTML={{
                    __html: notification.context, // Safely render rich HTML content
                  }}
                />
              </TableCell> */}
              <TableCell>{notification.subject}</TableCell>
              <TableCell>{notification.schedule}</TableCell>
              <TableCell>{notification.sent}</TableCell>
              <TableCell>
                <Tooltip title="Edit">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleEditNotification(notification)}
                    disabled={notification.sent === 'Sent'}
                    sx={{
                      marginLeft: 1,
                      backgroundColor: notification.sent === 'Sent' ? 'gray' : 'primary.main',
                    }}
                  >
                    <Edit />
                  </Button>
                </Tooltip>
                <Tooltip title="Delete">
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleDeleteNotification(notification)}
                    disabled={notification.sent === 'Sent'}
                    sx={{
                      marginLeft: 1,
                      backgroundColor: notification.sent === 'Sent' ? 'gray' : 'secondary.main',
                    }}
                  >
                    <Delete />
                  </Button>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>

          </Table>
        </TableContainer>
      )}

      {/* Edit Notification Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseDialogs} fullWidth maxWidth="sm">
        <DialogContent>
          <EditNotification
            notification={currentNotification}
            onSave={async (updatedNotification) => {
              try {
                // Convert the local time (schedule) to UTC before sending to the backend
                if (updatedNotification.schedule) {
                  const localTime = new Date(updatedNotification.schedule);
                  // Convert local time to UTC
                  updatedNotification.schedule = new Date(localTime.getTime() - localTime.getTimezoneOffset() * 60000).toISOString();
                }

                const token = localStorage.getItem('authToken');
                await fetch(`${BACKEND_BASE_URL}/api/notifications/${updatedNotification.id}/`, {
                  method: 'PATCH',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(updatedNotification),
                });

                toast.success('Notification updated successfully!');
                fetchNotifications(); // Refresh the list
              } catch (error) {
                console.error('Error updating notification:', error);
                toast.error('Failed to update notification.');
              } finally {
                handleCloseDialogs();
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Add Notification Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseDialogs} fullWidth maxWidth="sm">
      <DialogContent>
        <AddNotification
          onNotificationAdded={(newNotification) => {
            const formattedSchedule = newNotification.schedule
              ? `${newNotification.schedule.slice(0, 10)} ${newNotification.schedule.slice(11, 16)}`
              : "Immediate Notification";

            setNotifications((prevNotifications) => [
              ...prevNotifications,
              {
                id: newNotification.id,
                type: newNotification.type.charAt(0).toUpperCase() + newNotification.type.slice(1),
                context: newNotification.context,
                subject: newNotification.subject,
                schedule: formattedSchedule,
                sent: newNotification.is_sent ? "Sent" : "Pending",
                recipients: newNotification.recipients,
              },
            ]);
          }}
        />
      </DialogContent>
    </Dialog>

    </div>
  );
}
